import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { isGeoJSONSource } from '../../components/map/map-engine.js'
import { CACHED_GLYPHS } from '../tile-cache/vector-style-cache-service.js'

const SOURCE_ID = 'range-rings-source'
const LINE_LAYER_ID = 'range-rings-lines'
const LABEL_LAYER_ID = 'range-rings-labels'
const DEFAULT_OPACITY = 1.0
const DEFAULT_COLOR = '#E65100'
const RING_SEGMENTS = 64

interface Ring {
  meters: number
  label: string
}

const MI = 1609.344
const FT = 0.3048

const METRIC_RINGS: Ring[][] = [
  /* z0-4  */ [
    { meters: 200000, label: '200 km' },
    { meters: 500000, label: '500 km' },
    { meters: 1000000, label: '1000 km' },
  ],
  /* z5-6  */ [
    { meters: 25000, label: '25 km' },
    { meters: 50000, label: '50 km' },
    { meters: 100000, label: '100 km' },
    { meters: 200000, label: '200 km' },
  ],
  /* z7-8  */ [
    { meters: 10000, label: '10 km' },
    { meters: 25000, label: '25 km' },
    { meters: 50000, label: '50 km' },
    { meters: 100000, label: '100 km' },
  ],
  /* z9-10 */ [
    { meters: 2000, label: '2 km' },
    { meters: 5000, label: '5 km' },
    { meters: 10000, label: '10 km' },
    { meters: 25000, label: '25 km' },
  ],
  /* z11-12*/ [
    { meters: 500, label: '500 m' },
    { meters: 1000, label: '1 km' },
    { meters: 2000, label: '2 km' },
    { meters: 5000, label: '5 km' },
  ],
  /* z13-14*/ [
    { meters: 100, label: '100 m' },
    { meters: 200, label: '200 m' },
    { meters: 500, label: '500 m' },
    { meters: 1000, label: '1 km' },
  ],
  /* z15+  */ [
    { meters: 50, label: '50 m' },
    { meters: 100, label: '100 m' },
    { meters: 200, label: '200 m' },
    { meters: 500, label: '500 m' },
  ],
]

const IMPERIAL_RINGS: Ring[][] = [
  /* z0-4  */ [
    { meters: 100 * MI, label: '100 mi' },
    { meters: 250 * MI, label: '250 mi' },
    { meters: 500 * MI, label: '500 mi' },
  ],
  /* z5-6  */ [
    { meters: 25 * MI, label: '25 mi' },
    { meters: 50 * MI, label: '50 mi' },
    { meters: 100 * MI, label: '100 mi' },
    { meters: 250 * MI, label: '250 mi' },
  ],
  /* z7-8  */ [
    { meters: 5 * MI, label: '5 mi' },
    { meters: 10 * MI, label: '10 mi' },
    { meters: 25 * MI, label: '25 mi' },
    { meters: 50 * MI, label: '50 mi' },
  ],
  /* z9-10 */ [
    { meters: 1 * MI, label: '1 mi' },
    { meters: 2 * MI, label: '2 mi' },
    { meters: 5 * MI, label: '5 mi' },
    { meters: 10 * MI, label: '10 mi' },
  ],
  /* z11-12*/ [
    { meters: 1000 * FT, label: '1000 ft' },
    { meters: MI / 2, label: '½ mi' },
    { meters: 1 * MI, label: '1 mi' },
    { meters: 2 * MI, label: '2 mi' },
  ],
  /* z13-14*/ [
    { meters: 200 * FT, label: '200 ft' },
    { meters: 500 * FT, label: '500 ft' },
    { meters: 1000 * FT, label: '1000 ft' },
    { meters: MI / 2, label: '½ mi' },
  ],
  /* z15+  */ [
    { meters: 50 * FT, label: '50 ft' },
    { meters: 100 * FT, label: '100 ft' },
    { meters: 200 * FT, label: '200 ft' },
    { meters: 500 * FT, label: '500 ft' },
  ],
]

function ringDistances(zoom: number, unitSystem: 'metric' | 'imperial'): Ring[] {
  const table = unitSystem === 'metric' ? METRIC_RINGS : IMPERIAL_RINGS
  const z = Math.floor(zoom)
  if (z < 5) return table[0]
  if (z < 7) return table[1]
  if (z < 9) return table[2]
  if (z < 11) return table[3]
  if (z < 13) return table[4]
  if (z < 15) return table[5]
  return table[6]
}

function destinationPoint(
  lat: number,
  lng: number,
  distanceMeters: number,
  bearingDeg: number
): [number, number] {
  const R = 6371000
  const d = distanceMeters / R
  const brng = (bearingDeg * Math.PI) / 180
  const lat1 = (lat * Math.PI) / 180
  const lng1 = (lng * Math.PI) / 180

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng)
  )
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    )

  return [(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]
}

function buildRingFeatures(
  center: [number, number],
  zoom: number,
  unitSystem: 'metric' | 'imperial'
): GeoJSON.FeatureCollection {
  const [lng, lat] = center
  const features: GeoJSON.Feature[] = []
  const distances = ringDistances(zoom, unitSystem)
  const viewWidthMeters = (40075000 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)

  for (const { meters, label } of distances) {
    const coords: [number, number][] = []
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const bearing = (360 / RING_SEGMENTS) * i
      coords.push(destinationPoint(lat, lng, meters, bearing))
    }

    features.push({
      type: 'Feature',
      properties: { type: 'ring', label, distance: meters },
      geometry: { type: 'LineString', coordinates: coords },
    })

    // Label point at top of ring
    const labelPoint = destinationPoint(lat, lng, meters, 0)
    features.push({
      type: 'Feature',
      properties: { type: 'ring-label', label },
      geometry: { type: 'Point', coordinates: labelPoint },
    })
  }

  // Center crosshair
  const crossSize = viewWidthMeters * 0.01
  features.push({
    type: 'Feature',
    properties: { type: 'center' },
    geometry: {
      type: 'MultiLineString',
      coordinates: [
        [destinationPoint(lat, lng, crossSize, 0), destinationPoint(lat, lng, crossSize, 180)],
        [destinationPoint(lat, lng, crossSize, 90), destinationPoint(lat, lng, crossSize, 270)],
      ],
    },
  })

  return { type: 'FeatureCollection', features }
}

class RangeRingsService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private _center: [number, number] | null = null
  private _unitSystem: 'metric' | 'imperial' = 'imperial'
  private updateHandler = () => this.update()

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }
  get center(): [number, number] | null {
    return this._center
  }

  register(map: maplibregl.Map): void {
    this.map = map
  }

  unregister(): void {
    this.disable()
    this.map = null
  }

  enable(): void {
    if (!this.map || this._enabled) return
    this._enabled = true
    if (!this._center) {
      const c = this.map.getCenter()
      this._center = [c.lng, c.lat]
    }
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  disable(): void {
    if (!this.map) return
    this._enabled = false
    this.map.off('moveend', this.updateHandler)
    this.removeSourceAndLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  setCenter(lngLat: [number, number]): void {
    this._center = lngLat
    this.update()
  }

  setUnitSystem(system: 'metric' | 'imperial'): void {
    this._unitSystem = system
    this.update()
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (!this.map || !(this.map as unknown as Record<string, unknown>).style) return
    if (this.map.getLayer(LINE_LAYER_ID)) {
      this.map.setPaintProperty(LINE_LAYER_ID, 'line-opacity', this._opacity)
    }
    if (this.map.getLayer(LABEL_LAYER_ID)) {
      this.map.setPaintProperty(LABEL_LAYER_ID, 'text-opacity', this._opacity)
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) return
    this.removeSourceAndLayers()
    this.addSourceAndLayers()
    this.map.on('moveend', this.updateHandler)
    this.update()
  }

  private update(): void {
    if (!this.map || !this._enabled) return
    const c = this.map.getCenter()
    this._center = [c.lng, c.lat]
    const zoom = this.map.getZoom()
    const data = buildRingFeatures(this._center, zoom, this._unitSystem)
    const source = this.map.getSource(SOURCE_ID)
    if (isGeoJSONSource(source)) source.setData(data)
  }

  private addSourceAndLayers(): void {
    if (!this.map) return
    this.ensureGlyphs()

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }

    if (!this.map.getLayer(LINE_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['any', ['==', ['get', 'type'], 'ring'], ['==', ['get', 'type'], 'center']],
          paint: {
            'line-color': DEFAULT_COLOR,
            'line-opacity': this._opacity,
            'line-width': ['match', ['get', 'type'], 'center', 3, 2],
            'line-dasharray': [
              'match',
              ['get', 'type'],
              'center',
              ['literal', [1, 0]],
              ['literal', [6, 3]],
            ],
          },
        },
        'range-rings'
      )
    }

    if (!this.map.getLayer(LABEL_LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['==', ['get', 'type'], 'ring-label'],
          layout: {
            'text-field': ['get', 'label'],
            'text-font': ['Noto Sans Bold'],
            'text-size': 12,
            'text-anchor': 'bottom',
            'text-offset': [0, -0.5],
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': DEFAULT_COLOR,
            'text-opacity': this._opacity,
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2,
          },
        },
        'range-rings'
      )
    }
  }

  private ensureGlyphs(): void {
    if (!this.map) return
    const style = this.map.getStyle()
    if (!style.glyphs) {
      style.glyphs = CACHED_GLYPHS
      this.map.setStyle(style, { diff: true })
    }
  }

  private removeSourceAndLayers(): void {
    if (!this.map) return
    if (this.map.getLayer(LABEL_LAYER_ID)) this.map.removeLayer(LABEL_LAYER_ID)
    if (this.map.getLayer(LINE_LAYER_ID)) this.map.removeLayer(LINE_LAYER_ID)
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const rangeRingsService = new RangeRingsService()
